import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { merge } from 'rxjs';
import { CHAT_ERRORS, HelpChatStore, SpeechState } from '../services/help-chat.store';
import { SILENCE_LEVEL, VoiceRecorder, VoiceRecording } from '../services/voice-recorder';

/** Where the chosen microphone is remembered between visits. */
const MICROPHONE_STORAGE_KEY = 'help-chat.microphone';

/** A recording shorter than this cannot hold a question and is not sent. */
const MIN_RECORDING_MS = 800;

/**
 * The "Ask AI" tab of the help panel. Asks the AI help chat service and renders its markdown
 * answers; the conversation itself lives in HelpChatStore so it survives switching tabs.
 *
 * Voice is opt-in behind a switch in the composer. On, it adds a microphone for asking aloud and a
 * "Listen" button on every answer; the speech for an answer is fetched only when that button is
 * pressed.
 */
@Component({
  selector: 'adf-help-chat',
  imports: [AsyncPipe],
  templateUrl: './help-chat.component.html',
  styleUrl: './help-chat.component.scss',
})
export class HelpChatComponent implements OnInit, OnDestroy {
  private readonly _store = inject(HelpChatStore);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  readonly messages$ = this._store.messages$;
  readonly pending$ = this._store.pending$;
  readonly voiceEnabled$ = this._store.voiceEnabled$;
  readonly speech$ = this._store.speech$;

  draft = '';

  /** The microphone is open and a question is being recorded. */
  recording = false;

  /** Current input level while recording, 0..1, for the meter. */
  level = 0;

  /** The browser reports the open microphone as delivering nothing. */
  muted = false;

  /** Microphones to choose from; shown only when there is a choice to make. */
  microphones: MediaDeviceInfo[] = [];

  /** The chosen microphone's deviceId; empty for the browser's default. */
  microphoneId = readStoredMicrophone();

  /** Whether this browser can record at all; without it the switch still offers "Listen". */
  readonly canRecord = VoiceRecorder.isSupported();

  @ViewChild('log') private _log?: ElementRef<HTMLElement>;

  private readonly _recorder = new VoiceRecorder();
  private _voiceEnabled = false;
  private readonly _onDeviceChange = () => void this._refreshMicrophones();

  ngOnInit(): void {
    // Keep the newest message in view: a new question, the typing bubble and the answer each grow
    // the log at the bottom. Scrolled after the view has rendered the change.
    merge(this._store.messages$, this._store.pending$)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => setTimeout(() => this._scrollToEnd()));

    // The device list is worth having as soon as the voice is on: someone whose browser picked the
    // wrong microphone should see the choice before their first failed attempt, not after.
    this._store.voiceEnabled$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((enabled) => {
      this._voiceEnabled = enabled;

      if (enabled && this.canRecord) {
        void this._refreshMicrophones();
      }
    });

    navigator.mediaDevices?.addEventListener?.('devicechange', this._onDeviceChange);
  }

  ngOnDestroy(): void {
    // Switching tabs mid-recording must not leave the microphone open.
    this._recorder.cancel();
    navigator.mediaDevices?.removeEventListener?.('devicechange', this._onDeviceChange);
  }

  updateDraft(event: Event): void {
    this.draft = (event.target as HTMLInputElement).value;
  }

  send(): void {
    const text = this.draft.trim();

    if (!text || this._store.pending || this.recording) {
      return;
    }

    this.draft = '';
    void this._store.ask(text);
  }

  toggleVoice(event: Event): void {
    const enabled = (event.target as HTMLInputElement).checked;

    if (!enabled && this.recording) {
      this._recorder.cancel();
      this.recording = false;
    }

    this._store.setVoiceEnabled(enabled);
  }

  selectMicrophone(event: Event): void {
    const deviceId = (event.target as HTMLSelectElement).value;

    this.microphoneId = deviceId;
    storeMicrophone(deviceId);
  }

  /** One button: starts a recording, and pressed again stops it and sends it. */
  toggleRecording(): void {
    if (this.recording) {
      void this._finishRecording();
    } else {
      void this._startRecording();
    }
  }

  /** Whether this answer's speech is being fetched or played; null when it is neither. */
  playbackOf(messageId: number, speech: SpeechState | null): SpeechState['status'] | null {
    return speech?.messageId === messageId ? speech.status : null;
  }

  toggleSpeech(messageId: number): void {
    void this._store.speak(messageId);
  }

  clear(): void {
    this._store.clear();
  }

  /**
   * Routes in-app links in an answer through the router. The answer is plain [innerHTML], so its
   * links are ordinary anchors, and following one to /help/... would otherwise reload the app.
   */
  followLink(event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    const anchor = (event.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href');

    if (!href?.startsWith('/') || href.startsWith('//')) {
      return;
    }

    event.preventDefault();
    void this._router.navigateByUrl(href);
  }

  private async _startRecording(): Promise<void> {
    if (this._store.pending || this.recording) {
      return;
    }

    // An answer talking over the person asking the next question is no help.
    this._store.stopSpeaking();
    this.level = 0;
    this.muted = false;

    try {
      await this._recorder.start({
        deviceId: this.microphoneId,
        onLimit: () => void this._finishRecording(),
        onLevel: (level) => (this.level = level),
        onMuted: (muted) => (this.muted = muted),
      });
      this.recording = true;

      // Labels become available once the microphone has been allowed, so the list is only
      // complete from here on.
      void this._refreshMicrophones();
    } catch (error) {
      this.recording = false;
      this._store.reportVoiceError(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? CHAT_ERRORS.micDenied
          : CHAT_ERRORS.micUnavailable
      );
    }
  }

  private async _finishRecording(): Promise<void> {
    if (!this.recording) {
      return;
    }

    this.recording = false;

    const recording = await this._recorder.stop();
    const problem = this._checkRecording(recording);

    if (problem) {
      this._store.reportVoiceError(problem);
      return;
    }

    void this._store.askVoice(recording.blob);
  }

  /**
   * What is wrong with a recording, or null when it is worth sending. Catches the two ways a
   * recording comes back with nothing in it: the button was pressed twice in quick succession, or
   * the microphone the browser opened delivered no sound. Neither is worth a round trip that can
   * only end in "no speech recognised".
   */
  private _checkRecording(recording: VoiceRecording): string | null {
    if (!recording.blob.size || recording.durationMs < MIN_RECORDING_MS) {
      return CHAT_ERRORS.tooShort;
    }

    if (recording.muted || recording.peakLevel < SILENCE_LEVEL) {
      return this.microphones.length > 1 ? CHAT_ERRORS.noSoundChooseMicrophone : CHAT_ERRORS.noSound;
    }

    return null;
  }

  private async _refreshMicrophones(): Promise<void> {
    if (!this._voiceEnabled && !this.recording) {
      return;
    }

    const microphones = await VoiceRecorder.listMicrophones();
    this.microphones = microphones;

    // A remembered microphone that is gone is forgotten, so the browser's default is used rather
    // than a fallback the user cannot see.
    const chosen = this.microphoneId;
    if (chosen && microphones.length && !microphones.some((m) => m.deviceId === chosen)) {
      this.microphoneId = '';
      storeMicrophone('');
    }
  }

  private _scrollToEnd(): void {
    const log = this._log?.nativeElement;

    if (log) {
      log.scrollTop = log.scrollHeight;
    }
  }
}

function readStoredMicrophone(): string {
  try {
    return localStorage.getItem(MICROPHONE_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function storeMicrophone(deviceId: string): void {
  try {
    if (deviceId) {
      localStorage.setItem(MICROPHONE_STORAGE_KEY, deviceId);
    } else {
      localStorage.removeItem(MICROPHONE_STORAGE_KEY);
    }
  } catch {
    // Storage may be unavailable (private mode, quota); the choice then lasts for the page only.
  }
}
